import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney } from "../lib/api";
import { Page } from "../components/AppShell";
import {
  Scales, Briefcase, CheckCircle, CurrencyDollar,
  CalendarBlank, IdentificationCard, MapPin, ShieldCheck, FileText,
  ArrowRight,
} from "@phosphor-icons/react";

const PIPELINE_COLORS = {
  "New": "#94A3B8",
  "Assigned": "#3B82F6",
  "Attempted": "#F59E0B",
  "Served": "#22C55E",
  "Non-Service": "#EF4444",
  "Cancelled": "#6B7280",
};

export default function Dashboard() {
  const [data, setData] = useState({
    cases: [], jobs: [], statuses: [], calendar: [],
    locations: [], subjects: [], glba: [], pnl: null,
  });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      const [c, j, s, cal, loc, sub, g, p] = await Promise.allSettled([
        api.get("/cases"), api.get("/jobs"), api.get("/jobs/statuses"),
        api.get("/calendar"), api.get("/locations"), api.get("/subjects"),
        api.get("/glba-verifications"), api.get("/accounting/reports/pnl"),
      ]);
      setData({
        cases: c.value?.data || [],
        jobs: j.value?.data || [],
        statuses: s.value?.data || [],
        calendar: cal.value?.data || [],
        locations: loc.value?.data || [],
        subjects: sub.value?.data || [],
        glba: g.value?.data || [],
        pnl: p.value?.data || null,
      });
      setLoaded(true);
    })();
  }, []);

  const statusById = useMemo(() => Object.fromEntries(data.statuses.map(s => [s.id, s])), [data.statuses]);
  const statusByName = useMemo(() => Object.fromEntries(data.statuses.map(s => [s.name, s])), [data.statuses]);

  const monthStart = useMemo(() => {
    const d = new Date(); d.setDate(1); d.setHours(0, 0, 0, 0); return d;
  }, []);
  const weekEnd = useMemo(() => {
    const d = new Date(); d.setHours(23, 59, 59, 999); d.setDate(d.getDate() + 7); return d;
  }, []);
  const now = useMemo(() => new Date(), []);

  // ---- KPIs ----
  const openJobs = useMemo(() => data.jobs.filter(j => {
    const st = statusById[j.job_status_id];
    return !st || !st.is_terminal;
  }), [data.jobs, statusById]);

  const servedThisMonth = useMemo(() => data.jobs.filter(j => {
    const st = statusById[j.job_status_id];
    if (st?.name !== "Served") return false;
    const ref = j.served_at || j.created_at;
    return ref && new Date(ref) >= monthStart;
  }), [data.jobs, statusById, monthStart]);

  const revenueMTD = useMemo(() => servedThisMonth.reduce((sum, j) => sum + (j.price || 0), 0), [servedThisMonth]);
  const revenueLifetime = data.pnl?.total_revenue || 0;

  // ---- Pipeline ----
  const pipeline = useMemo(() => {
    const order = ["New", "Assigned", "Attempted", "Served", "Non-Service", "Cancelled"];
    return order.map(name => {
      const st = statusByName[name];
      const count = data.jobs.filter(j => {
        if (!j.job_status_id && name === "New") return true;
        return st && j.job_status_id === st.id;
      }).length;
      return { name, count, color: PIPELINE_COLORS[name] };
    });
  }, [data.jobs, statusByName]);

  const pipelineMax = Math.max(1, ...pipeline.map(p => p.count));

  // ---- Calendar (next 7 days, future + today) ----
  const upcoming = useMemo(() => {
    return data.calendar
      .filter(e => e.start_time && new Date(e.start_time) >= new Date(now.getTime() - 60 * 60 * 1000) && new Date(e.start_time) <= weekEnd)
      .sort((a, b) => new Date(a.start_time) - new Date(b.start_time))
      .slice(0, 6);
  }, [data.calendar, now, weekEnd]);

  // ---- Recent jobs ----
  const recentJobs = useMemo(() => {
    return [...data.jobs]
      .sort((a, b) => new Date(b.received_at || b.created_at || 0) - new Date(a.received_at || a.created_at || 0))
      .slice(0, 6);
  }, [data.jobs]);

  const caseById = useMemo(() => Object.fromEntries(data.cases.map(c => [c.id, c])), [data.cases]);

  if (!loaded) return <div className="p-8 text-muted-foreground">Loading dashboard…</div>;

  return (
    <Page
      title="Operations overview"
      subtitle="LOGBOOK LEGAL / PROCESS SERVING"
      testid="dashboard-page"
    >

      {/* ---- Hero KPIs ---- */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        <Kpi label="ACTIVE CASES" value={data.cases.length} sub="cases on file" icon={Scales} to="/cases" testid="kpi-cases" />
        <Kpi label="OPEN JOBS" value={openJobs.length} sub={`of ${data.jobs.length} total`} icon={Briefcase} to="/jobs" testid="kpi-open-jobs" />
        <Kpi label="SERVED THIS MONTH" value={servedThisMonth.length} sub={fmtMoney(revenueMTD) + " revenue"} icon={CheckCircle} to="/jobs" testid="kpi-served" positive />
        <Kpi label="REVENUE (LIFETIME)" value={fmtMoney(revenueLifetime)} sub="from accounting" icon={CurrencyDollar} to="/accounting" testid="kpi-revenue" mono />
      </div>

      {/* ---- Job pipeline ---- */}
      <div className="surface p-6 mb-6" data-testid="job-pipeline">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="label-xs">JOB PIPELINE</div>
            <h2 className="heading text-xl">Status distribution</h2>
          </div>
          <Link to="/jobs" className="text-xs mono text-primary hover:underline">VIEW ALL JOBS →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {pipeline.map(p => (
            <div key={p.name} className="border border-border p-3" data-testid={`pipeline-${p.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
                <span className="text-xs mono text-muted-foreground uppercase tracking-wide">{p.name}</span>
              </div>
              <div className="heading text-2xl mono">{p.count}</div>
              <div className="mt-2 h-1 bg-muted overflow-hidden">
                <div className="h-full" style={{ width: `${(p.count / pipelineMax) * 100}%`, background: p.color }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ---- Secondary counters ---- */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Counter label="LOCATIONS" n={data.locations.length} icon={MapPin} to="/locations" testid="counter-locations" />
        <Counter label="SUBJECTS" n={data.subjects.length} icon={IdentificationCard} to="/subjects" testid="counter-subjects" />
        <Counter label="UPCOMING (7D)" n={upcoming.length} icon={CalendarBlank} to="/calendar" testid="counter-upcoming" />
        <Counter label="GLBA LOG" n={data.glba.length} icon={ShieldCheck} to="/glba" testid="counter-glba" />
      </div>

      {/* ---- Recent jobs + upcoming calendar ---- */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Panel title="RECENT JOBS" to="/jobs" testid="recent-jobs">
          {recentJobs.length === 0 ? (
            <Empty>No jobs yet. Create your first one from the <Link to="/jobs" className="text-primary">Jobs</Link> page.</Empty>
          ) : recentJobs.map(j => {
            const st = statusById[j.job_status_id];
            const c = caseById[j.case_id];
            return (
              <div key={j.id} className="flex items-center justify-between py-2 border-b border-border last:border-0" data-testid={`recent-job-${j.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="mono text-sm truncate">{j.job_number || `JOB-${j.id.slice(0, 6)}`}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {c ? `${c.case_number || c.plaintiff || ""} ${c.plaintiff ? "v. " + c.defendant : ""}`.trim() : "—"}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {j.price > 0 && <span className="mono text-sm">{fmtMoney(j.price)}</span>}
                  <PipelineBadge status={st?.name || "New"} />
                </div>
              </div>
            );
          })}
        </Panel>

        <Panel title="UPCOMING — NEXT 7 DAYS" to="/calendar" testid="upcoming-events">
          {upcoming.length === 0 ? (
            <Empty>Nothing scheduled this week. Open the <Link to="/calendar" className="text-primary">Calendar</Link> to add an event.</Empty>
          ) : upcoming.map(e => (
            <div key={e.id} className="flex items-center justify-between py-2 border-b border-border last:border-0" data-testid={`upcoming-${e.id}`}>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{e.type || "Event"} {e.details ? <span className="text-muted-foreground">— {e.details}</span> : null}</div>
                <div className="text-xs text-muted-foreground mono">
                  {new Date(e.start_time).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                </div>
              </div>
              <FileText size={14} className="text-muted-foreground flex-shrink-0" />
            </div>
          ))}
        </Panel>
      </div>
    </Page>
  );
}

const Kpi = ({ label, value, sub, icon: I, to, testid, mono = false, positive = false }) => {
  const card = (
    <div className="surface p-5 h-full hover:border-primary/40 transition-colors group" data-testid={testid}>
      <div className="flex items-start justify-between mb-3">
        <span className="label-xs">{label}</span>
        <I size={16} className={positive ? "text-green-600" : "text-muted-foreground"} weight={positive ? "fill" : "regular"} />
      </div>
      <div className={`heading text-3xl ${mono ? "mono" : ""}`}>{value}</div>
      <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
        <span>{sub}</span>
        {to && <ArrowRight size={10} className="opacity-0 group-hover:opacity-100 transition-opacity" />}
      </div>
    </div>
  );
  return to ? <Link to={to}>{card}</Link> : card;
};

const Counter = ({ label, n, icon: I, to, testid }) => {
  const card = (
    <div className="surface p-4 flex items-center justify-between hover:border-primary/40 transition-colors" data-testid={testid}>
      <div>
        <div className="label-xs mb-1">{label}</div>
        <div className="heading text-2xl mono">{n}</div>
      </div>
      <I size={22} weight="thin" />
    </div>
  );
  return to ? <Link to={to}>{card}</Link> : card;
};

const Panel = ({ title, to, children, testid }) => (
  <div className="surface p-5" data-testid={testid}>
    <div className="flex items-center justify-between mb-3">
      <div className="label-xs">{title}</div>
      {to && <Link to={to} className="text-xs mono text-primary hover:underline">VIEW ALL →</Link>}
    </div>
    <div>{children}</div>
  </div>
);

const Empty = ({ children }) => (
  <div className="text-sm text-muted-foreground py-6 text-center">{children}</div>
);

const PipelineBadge = ({ status }) => {
  const color = PIPELINE_COLORS[status] || "#6B7280";
  return (
    <span
      className="text-[10px] mono uppercase tracking-wider px-2 py-1 border"
      style={{ color, borderColor: color + "55" }}
      data-testid={`pipeline-badge-${status.toLowerCase().replace(/[^a-z]+/g, "-")}`}
    >
      {status}
    </span>
  );
};
